import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { LayoutGrid, Link2, List, Search, Plus, Users, TrendingUp, MessageSquare, Trophy, Trash2, Loader2, AlertTriangle, CheckSquare, Tag, X, Send, Megaphone, Sparkles, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PageHeader } from '@/components/layout/page-header'
import { FieldHint } from '@/components/field-hint'
import { SectionHelp } from '@/components/help/section-help'
import { cn } from '@/lib/utils'
import { AnimatedNumber } from '@/components/animated-number'
import { KanbanBoard } from './kanban-board'
import { LeadsTable } from './leads-table'
import { LeadDetail } from './lead-detail'
import { AddLeadDialog } from './add-lead-dialog'
import { AddToCampaignDialog } from './add-to-campaign-dialog'
import { useLeads, useLeadStats, useDeleteAllLeads, useBulkDeleteLeads, useUpdateLead, useBulkEnrichLeads, useExportLeadsCsv } from './use-leads'
import type { BulkEnrichResult } from './use-leads'
import { boardCapNotice } from './board-cap'
import { remainingImported, takeImported } from './just-imported'
import { useCampaigns } from '@/features/campaigns/use-campaigns'
import { WizardReturnBanner } from '@/features/campaigns/wizard-return-banner'
import { useApi } from '@/hooks/use-api'
import { KANBAN_COLUMNS, type Lead, type LeadStatus, type ViewMode } from './types'

const VIEW_MODE_KEY = 'leads_view_mode'

/**
 * Sentinel for the campaign filter's "No campaign" option (leads with a null
 * campaign_id). Filtered client-side: the leads API's campaign_id query param
 * has no "is null" form, and the page already holds the full pipeline.
 */
const NO_CAMPAIGN = 'none'

type StatCardProps = {
  readonly label: string
  readonly value: string | number
  readonly icon: React.ComponentType<{ className?: string }>
  readonly trend?: string
  readonly hint?: string
}

function StatCard({ label, value, icon: Icon, trend, hint }: StatCardProps) {
  // Render numbers via AnimatedNumber (drop-in + tween); pass-through strings
  // like percentages ("12%") or em-dashes without animating.
  const isNumeric = typeof value === 'number'
  return (
    <Card className="group relative flex-1 overflow-hidden">
      <div
      aria-hidden="true"
      className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-[var(--color-cobalt)]/0 blur-2xl transition-all duration-300 group-hover:bg-[var(--color-cobalt)]/15"
    />
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <p className="eyebrow text-[var(--color-silver)]">{label}</p>
            {hint && <FieldHint label={`What is ${label}?`}>{hint}</FieldHint>}
          </div>
          <Icon className="h-4 w-4 text-[var(--color-cobalt-bright)]" />
        </div>
        <div className="mt-1 overflow-clip-y text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
          {isNumeric ? (
              <AnimatedNumber
              value={value}
              format={(n) => Math.round(n).toLocaleString()}
              className="inline-block animate-digit-drop"
            />
          ) : (
            <span className="inline-block animate-digit-drop">{value}</span>
          )}
        </div>
        {trend && <p className="mt-0.5 text-xs text-[var(--color-silver)]">{trend}</p>}
      </CardContent>
    </Card>
  )
}

export function LeadsPage() {
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem(VIEW_MODE_KEY)
      return (saved as ViewMode | null) ?? 'kanban'
    } catch {
      return 'kanban'
    }
  })
  const [search, setSearch] = useState('')
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [addLeadOpen, setAddLeadOpen] = useState(false)
  const [deleteAllOpen, setDeleteAllOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const exportLeadsCsv = useExportLeadsCsv()

  // Deep-link support: the campaign wizard's "Import by URL" escape hatch
  // navigates to `/leads?add=url` so the user lands directly in the URL
  // import instead of hunting for the button. Strip the param after firing
  // (same idiom as /content?new=1) so closing the dialog doesn't re-open it.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.get('add') !== 'url') return
    setAddLeadOpen(true)
    const next = new URLSearchParams(searchParams)
    next.delete('add')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  // Deep link from a notification: /leads?id=<leadId> opens that lead's
  // detail panel. The param is stripped once the lead is loaded.
  const apiFetch = useApi()
  const deepLinkId = searchParams.get('id')
  useEffect(() => {
    if (!deepLinkId) return
    let cancelled = false
    void apiFetch<Lead>(`/api/leads/${encodeURIComponent(deepLinkId)}`).then((res) => {
      if (cancelled) return
      if (res.success && res.data) {
        setSelectedLead(res.data)
        setDetailOpen(true)
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.delete('id')
        return next
      }, { replace: true })
    })
    return () => {
      cancelled = true
    }
  }, [deepLinkId, apiFetch, setSearchParams])

  // Selection mode — when active, lead clicks toggle a checkbox instead of
  // opening the detail panel. Set is the source of truth (cheap membership
  // checks for both kanban cards and table rows).
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [addCampaignOpen, setAddCampaignOpen] = useState(false)
  // Campaign filter — 'all' shows the whole pipeline; a campaign id narrows
  // the board/table server-side (the API filters on campaign_id); NO_CAMPAIGN
  // shows leads not assigned to any campaign. The API's campaign_id param
  // can't express "is null", so the no-campaign case fetches the full
  // pipeline (already one request, server max 500) and filters client-side.
  const [campaignFilter, setCampaignFilter] = useState<string>('all')
  const isServerFiltered = campaignFilter !== 'all' && campaignFilter !== NO_CAMPAIGN

  const { data: fetchedLeads = [], isLoading: leadsLoading } = useLeads({
    search: search || undefined,
    campaign: isServerFiltered ? campaignFilter : undefined,
  })
  const leads = useMemo(
    () =>
      campaignFilter === NO_CAMPAIGN
        ? fetchedLeads.filter((l) => !l.campaign_id)
        : fetchedLeads,
    [campaignFilter, fetchedLeads],
  )

  // Count for the "No campaign (N)" dropdown label. Derived from the
  // unfiltered fetch; while a specific campaign filter is active (when that
  // fetch isn't running) the last-known count is kept rather than dropped.
  const [noCampaignCount, setNoCampaignCount] = useState<number | null>(null)
  useEffect(() => {
    if (isServerFiltered) return
    setNoCampaignCount(fetchedLeads.filter((l) => !l.campaign_id).length)
  }, [isServerFiltered, fetchedLeads])
  const { data: stats } = useLeadStats()
  // fetchedLeads, not leads: the "No campaign" view filters a capped fetch too.
  const capNotice = leadsLoading
    ? null
    : boardCapNotice({ fetched: fetchedLeads.length, total: stats?.total, campaignFiltered: isServerFiltered })
  const { data: allCampaigns = [] } = useCampaigns()
  const filterableCampaigns = allCampaigns.filter((c) => c.status !== 'deleted')
  const deleteAll = useDeleteAllLeads()
  const bulkDelete = useBulkDeleteLeads()
  const updateLead = useUpdateLead()
  // Bulk AI research (owner request 2026-09-05): "select all Not Researched
  // → Enrich ALL". The worker skips already-researched rows for free, so
  // the button is safe on a mixed selection.
  const bulkEnrich = useBulkEnrichLeads()
  const [enrichSummary, setEnrichSummary] = useState<BulkEnrichResult | null>(null)

  // Drop selections that no longer correspond to a fetched lead — happens
  // after a delete or after the list re-fetches with a search filter applied.
  const visibleIds = useMemo(() => new Set(leads.map((l) => l.id)), [leads])
  const effectiveSelected = useMemo(
    () => new Set([...selectedIds].filter((id) => visibleIds.has(id))),
    [selectedIds, visibleIds],
  )

  // Hand-off from a bulk add on Lead Search (/leads?imported=1, see
  // just-imported.ts): select exactly the new leads, with Research ready.
  const [justImported, setJustImported] = useState<ReadonlySet<string>>(() => new Set())
  // True once a Research click has run on the imported leads and some were
  // stopped by the enrich rate limit; the banner then offers the rest.
  const [importedPartlyDone, setImportedPartlyDone] = useState(false)
  useEffect(() => {
    if (searchParams.get('imported') !== '1') return
    const ids = takeImported()
    if (ids.length > 0) {
      setJustImported(new Set(ids))
      setImportedPartlyDone(false)
      setSelectMode(true)
      setSelectedIds(new Set(ids))
    }
    const next = new URLSearchParams(searchParams)
    next.delete('imported')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])
  // The list holds the newest 500 leads and follows the search and campaign
  // filters, so some imported leads may not be on screen. Research goes to
  // every remembered id except the ones visibly researched already (the
  // worker skips researched leads for free anyway), and the banner says how
  // many are not shown.
  const importedToResearch = useMemo(() => {
    const researched = new Set(leads.filter((l) => l.enriched_at).map((l) => l.id))
    return [...justImported].filter((id) => !researched.has(id))
  }, [leads, justImported])
  const importedHidden = useMemo(
    () => [...justImported].filter((id) => !visibleIds.has(id)).length,
    [justImported, visibleIds],
  )

  function toggleSelectMode() {
    setSelectMode((m) => {
      if (m) setSelectedIds(new Set())
      return !m
    })
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function selectAllVisible() {
    setSelectedIds(new Set(leads.map((l) => l.id)))
  }

  const notResearched = useMemo(() => leads.filter((l) => !l.enriched_at), [leads])

  function selectNotResearched() {
    setSelectedIds(new Set(notResearched.map((l) => l.id)))
  }

  async function handleBulkEnrich(ids: readonly string[] = [...effectiveSelected]) {
    if (ids.length === 0) return
    setEnrichSummary(null)
    try {
      const summary = await bulkEnrich.mutateAsync(ids)
      setEnrichSummary(summary)
      // Keep the imported leads the rate limit stopped, so the banner can offer
      // "Research the remaining N" instead of losing the list.
      const left = remainingImported(justImported, summary.results)
      setJustImported(left)
      setImportedPartlyDone(left.size > 0)
      setSelectedIds(left.size > 0 ? new Set(left) : new Set())
    } catch (err) {
      setEnrichSummary({
        results: [], enriched: 0, fresh: 0, failed: ids.length, rate_limited: 0, not_found: 0,
        stopped_reason: err instanceof Error ? err.message : 'Bulk research failed',
      })
    }
  }

  function clearSelection() {
    setSelectedIds(new Set())
  }

  async function handleBulkDelete() {
    const ids = [...effectiveSelected]
    if (ids.length === 0) return
    await bulkDelete.mutateAsync(ids).catch(() => undefined)
    setSelectedIds(new Set())
  }

  function handleBulkStatusChange(status: LeadStatus) {
    const ids = [...effectiveSelected]
    if (ids.length === 0) return
    for (const id of ids) {
      updateLead.mutate({ id, updates: { status } })
    }
    setSelectedIds(new Set())
  }

  async function handleConfirmDeleteAll() {
    try {
      await deleteAll.mutateAsync()
      setDeleteAllOpen(false)
      setConfirmText('')
    } catch { /* error surfaced in dialog */ }
  }

  function changeViewMode(mode: ViewMode) {
    setViewMode(mode)
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode)
    } catch {
      // localStorage not available
    }
  }

  function handleLeadClick(lead: Lead) {
    // In select mode, the click toggles selection instead of opening detail.
    if (selectMode) {
      toggleSelect(lead.id)
      return
    }
    setSelectedLead(lead)
    setDetailOpen(true)
  }

  function handleDetailClose(open: boolean) {
    setDetailOpen(open)
    if (!open) {
      // Small delay before clearing lead so exit animation completes
      setTimeout(() => setSelectedLead(null), 300)
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Lead Pipeline"
        subtitle="Manage and track your leads through the pipeline."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <SectionHelp sectionKey="leads" />
            {/* Select toggle — only useful when there's something to select. */}
            {leads.length > 0 && (
              <Button
                size="sm"
                variant={selectMode ? 'default' : 'outline'}
                className="text-xs"
                onClick={toggleSelectMode}
                data-tour="leads-select"
              >
                {selectMode ? <X className="h-3.5 w-3.5" /> : <CheckSquare className="h-3.5 w-3.5" />}
                {selectMode ? 'Exit select' : 'Select'}
              </Button>
            )}
            {/* Delete-all — only offered when there's actually something to delete. */}
            {(stats?.total ?? 0) > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={() => setDeleteAllOpen(true)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete all
              </Button>
            )}
            {/* Export. Nothing to export with an empty pipeline. */}
            {(stats?.total ?? 0) > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs"
                onClick={() => exportLeadsCsv.mutate()}
                disabled={exportLeadsCsv.isPending}
              >
                {exportLeadsCsv.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Export CSV
              </Button>
            )}
            <Button size="sm" variant="outline" className="text-xs" onClick={() => setAddLeadOpen(true)}>
              <Link2 className="h-3.5 w-3.5" />
              Add with URL
            </Button>
            <Button
              size="sm"
              className="text-xs"
              onClick={() => navigate('/leads/discover')}
              data-tour="leads-add"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Lead
            </Button>
          </div>
        }
      />

      {/* Export error. No toast system exists in this dashboard yet, so this
          is a lightweight inline banner instead; it self-clears on the next
          export attempt (mutate() resets isError). */}
      {exportLeadsCsv.isError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {exportLeadsCsv.error instanceof Error ? exportLeadsCsv.error.message : 'Export failed'}
        </div>
      )}

      {/* Mid-campaign-setup detour? Offer the way back to the wizard. */}
      <WizardReturnBanner />

      {importedToResearch.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--color-cobalt)]/30 bg-[var(--color-cobalt)]/5 px-4 py-3"
          data-testid="imported-research-banner"
        >
          <span className="text-sm">
            {importedPartlyDone
              ? `${importedToResearch.length} of the leads you just added still need research.`
              : importedToResearch.length === 1
                ? 'The lead you just added has no research yet.'
                : `The ${importedToResearch.length} leads you just added have no research yet.`}{' '}
            {importedHidden > 0
              ? `${importedHidden} of them ${importedHidden === 1 ? 'is' : 'are'} not in the list below (it shows your newest 500 leads and follows your filters); Research still covers them.`
              : 'They are selected below.'}{' '}
            Research gives the AI and your Claude more than a name and headline to write from. It runs a few
            leads a minute, with a daily limit, so a big import can take a few clicks.
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => void handleBulkEnrich(importedToResearch)}
              disabled={bulkEnrich.isPending}
            >
              <Sparkles className="h-3.5 w-3.5" />
              {importedPartlyDone ? `Research the remaining ${importedToResearch.length}` : `Research ${importedToResearch.length}`}
            </Button>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setJustImported(new Set())}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Bulk action bar — visible whenever select mode is on, so the user
          can see "0 selected" and the Select-all affordance immediately
          rather than after their first click. */}
      {selectMode && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--color-cobalt)]/30 bg-[var(--color-cobalt)]/5 px-4 py-2">
          <span className="text-sm font-medium">
            {effectiveSelected.size} selected
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs"
            onClick={selectAllVisible}
            disabled={leads.length === 0 || effectiveSelected.size === leads.length}
          >
            Select all ({leads.length})
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs"
            onClick={selectNotResearched}
            disabled={notResearched.length === 0}
            title="Select every lead the AI has not researched yet"
          >
            Select not researched ({notResearched.length})
          </Button>
          {effectiveSelected.size > 0 && (
            <>
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={() => setAddCampaignOpen(true)}
              >
                <Send className="h-3 w-3" />
                Add to Campaign
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="h-8 text-xs"
                onClick={() => void handleBulkEnrich()}
                disabled={bulkEnrich.isPending}
                title="Runs AI research on the selected leads. Already-researched leads are skipped."
              >
                {bulkEnrich.isPending ? (
                  <><Loader2 className="h-3 w-3 animate-spin" /> Researching…</>
                ) : (
                  <><Sparkles className="h-3 w-3" /> Research {effectiveSelected.size}</>
                )}
              </Button>
              <Select onValueChange={(v) => handleBulkStatusChange(v as LeadStatus)}>
                <SelectTrigger className="h-8 w-44 text-xs">
                  <div className="flex items-center gap-1.5">
                    <Tag className="h-3 w-3" />
                    <SelectValue placeholder="Change status" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {KANBAN_COLUMNS.map((col) => (
                    <SelectItem key={col.id} value={col.id} className="text-xs">
                      {col.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="destructive"
                className="h-8 text-xs"
                onClick={handleBulkDelete}
                disabled={bulkDelete.isPending}
              >
                {bulkDelete.isPending ? (
                  <><Loader2 className="h-3 w-3 animate-spin" /> Deleting…</>
                ) : (
                  <><Trash2 className="h-3 w-3" /> Delete {effectiveSelected.size}</>
                )}
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={clearSelection}>
                Clear
              </Button>
            </>
          )}
        </div>
      )}

      {enrichSummary && (
        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-2 rounded-lg border px-4 py-2 text-xs',
            enrichSummary.stopped_reason || enrichSummary.failed > 0
              ? 'border-amber-500/40 bg-amber-500/5'
              : 'border-emerald-500/40 bg-emerald-500/5',
          )}
        >
          <span>
            Research done: {enrichSummary.enriched} researched
            {enrichSummary.fresh > 0 && `, ${enrichSummary.fresh} already researched (skipped, no allowance used)`}
            {enrichSummary.failed > 0 && `, ${enrichSummary.failed} failed`}
            {enrichSummary.rate_limited > 0 && `, ${enrichSummary.rate_limited} not run`}
            {enrichSummary.stopped_reason && ` — ${enrichSummary.stopped_reason}`}
          </span>
          <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setEnrichSummary(null)} aria-label="Dismiss">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Stats row */}
      <div className="flex flex-col gap-3 sm:flex-row" data-tour="leads-stats">
        <StatCard
          label="Total Leads"
          value={stats?.total ?? 0}
          icon={Users}
          trend={stats ? `${stats.by_status.won} won` : undefined}
          hint="Every lead in your pipeline across all stages and campaigns."
        />
        <StatCard
          label="This Week"
          value={stats?.this_week ?? 0}
          icon={TrendingUp}
          trend="New leads added"
          hint="Leads added in the last 7 days."
        />
        <StatCard
          label="Reply Rate"
          value={stats ? `${stats.reply_rate}%` : '-'}
          icon={MessageSquare}
          trend="Messages with replies"
          hint="The share of contacted leads who have replied at least once."
        />
        <StatCard
          label="Win Rate"
          value={stats ? `${stats.win_rate}%` : '-'}
          icon={Trophy}
          trend="Qualified to won"
          hint="The share of qualified leads you have marked Won."
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* View toggle */}
        <div className="flex rounded-md border p-0.5" data-tour="leads-view-toggle">
          <button
            type="button"
            onClick={() => changeViewMode('kanban')}
            className={cn(
              'flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs transition-colors',
              viewMode === 'kanban'
                ? 'bg-background shadow-sm text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground',
            )}
            aria-label="Kanban view"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Kanban
          </button>
          <button
            type="button"
            onClick={() => changeViewMode('table')}
            className={cn(
              'flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs transition-colors',
              viewMode === 'table'
                ? 'bg-background shadow-sm text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground',
            )}
            aria-label="Table view"
          >
            <List className="h-3.5 w-3.5" />
            Table
          </button>
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-xs" data-tour="leads-filters">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>

        {/* Campaign filter */}
        {filterableCampaigns.length > 0 && (
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="h-8 w-56 text-xs">
              <div className="flex items-center gap-1.5">
                <Megaphone className="h-3 w-3" />
                <SelectValue placeholder="All campaigns" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                All campaigns
              </SelectItem>
              <SelectItem value={NO_CAMPAIGN} className="text-xs">
                No campaign{noCampaignCount !== null ? ` (${noCampaignCount})` : ''}
              </SelectItem>
              {filterableCampaigns.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-xs">
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {capNotice && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-2 text-xs">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
          <span>{capNotice}</span>
        </div>
      )}

      {/* Main content */}
      <div data-tour="leads-board">
        {leadsLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
            Loading leads...
          </div>
        ) : viewMode === 'kanban' ? (
          <KanbanBoard
            leads={leads}
            onLeadClick={handleLeadClick}
            selectMode={selectMode}
            selectedIds={effectiveSelected}
            onToggleSelect={toggleSelect}
          />
        ) : (
          <LeadsTable
            leads={leads}
            onLeadClick={handleLeadClick}
            selectMode={selectMode}
            selectedIds={effectiveSelected}
            onToggleSelect={toggleSelect}
          />
        )}
      </div>

      {/* Lead detail slide-over */}
      <LeadDetail
        lead={selectedLead}
        open={detailOpen}
        onOpenChange={handleDetailClose}
      />
      <AddLeadDialog
        open={addLeadOpen}
        onOpenChange={setAddLeadOpen}
        onLeadCreated={(lead) => {
          setSelectedLead(lead)
          setDetailOpen(true)
        }}
      />

      {/* Bulk "Add to Campaign" for the current selection. Clearing the
          selection on success keeps the bulk bar honest (the leads are now
          assigned; nothing left to act on). */}
      <AddToCampaignDialog
        leadIds={[...effectiveSelected]}
        leads={leads}
        open={addCampaignOpen}
        onOpenChange={setAddCampaignOpen}
        onAdded={() => setSelectedIds(new Set())}
      />

      {/* Delete-all confirmation. Requires typing the word DELETE so a
          misclick can't wipe the pipeline. Running while disabled is
          physically impossible — both the button and the input are
          locked while the mutation is in flight. */}
      <Dialog
        open={deleteAllOpen}
        onOpenChange={(o) => {
          if (deleteAll.isPending) return
          setDeleteAllOpen(o)
          if (!o) setConfirmText('')
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <DialogTitle>Delete ALL leads?</DialogTitle>
            </div>
            <DialogDescription className="pt-2 text-foreground/90">
              This permanently removes{' '}
              <span className="font-semibold">
                {stats?.total ?? 0} lead{(stats?.total ?? 0) === 1 ? '' : 's'}
              </span>
              {' '}along with their conversation history, signals, and campaign
              assignments. <strong>This cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <label className="text-xs text-muted-foreground">
              Type <span className="font-mono font-semibold">DELETE</span> to confirm:
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              disabled={deleteAll.isPending}
              autoFocus
            />
            {deleteAll.error && (
              <p className="text-xs text-destructive">
                {deleteAll.error instanceof Error ? deleteAll.error.message : 'Failed to delete'}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteAllOpen(false)
                setConfirmText('')
              }}
              disabled={deleteAll.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteAll}
              disabled={deleteAll.isPending || confirmText !== 'DELETE'}
            >
              {deleteAll.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Deleting…</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-1" /> Delete {stats?.total ?? 0} leads</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
